import { useCallback, useEffect, useRef, useState } from "react";
import {
  useVoiceAssistant,
  useLocalParticipant,
  useRoomContext,
} from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import "./CallUI.css";

interface Msg { id: string; role: "agent" | "caller"; text: string; }

export function CallUI({ title, subtitle, onHangUp }: {
  title: string; subtitle: string; onHangUp: () => void;
}) {
  const room = useRoomContext();
  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
  const { state: agentState, agentTranscriptions } = useVoiceAssistant();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const seenIds  = useRef<Set<string>>(new Set());
  const scrollEl = useRef<HTMLDivElement>(null);

  const speaking = agentState === "speaking";

  // Agent transcription (final segments only)
  useEffect(() => {
    agentTranscriptions.forEach(seg => {
      if (!seg.final || seenIds.current.has(seg.id)) return;
      seenIds.current.add(seg.id);
      setMsgs(p => [...p, { id: seg.id, role: "agent", text: seg.text }]);
    });
  }, [agentTranscriptions]);

  // Caller transcript from room data messages
  useEffect(() => {
    const handler = (payload: Uint8Array, _p: unknown, _k: unknown, topic?: string) => {
      if (topic !== "lk.transcription" && topic !== "lk-chat-topic") return;
      try {
        const m = JSON.parse(new TextDecoder().decode(payload));
        const text: string = m.text ?? m.message ?? "";
        if (!text || m.final === false) return;
        setMsgs(p => [...p, { id: crypto.randomUUID(), role: "caller", text }]);
      } catch { /* ignore */ }
    };
    room.on(RoomEvent.DataReceived, handler);
    return () => { room.off(RoomEvent.DataReceived, handler); };
  }, [room]);

  // Auto-scroll
  useEffect(() => {
    const el = scrollEl.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs]);

  const toggleMic = useCallback(() =>
    localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled),
    [localParticipant, isMicrophoneEnabled]);

  const hangUp = useCallback(() => { room.disconnect(); onHangUp(); }, [room, onHangUp]);

  const statusLabel: Record<string, string> = {
    disconnected: "Connecting agent…", connecting: "Connecting…",
    "pre-connect-buffering": "Connecting…", initializing: "Initialising…",
    listening: "Listening", thinking: "Thinking…", speaking: "Speaking…", failed: "Failed",
  };

  return (
    <div className="cui-root">
      {/* ── Status bar ── */}
      <div className="cui-status">
        <Waveform active={speaking} />
        <div className="cui-status-text">
          <span className="cui-title">{title}</span>
          <span className="cui-sub">{statusLabel[agentState] ?? "Connecting…"}</span>
        </div>
        <div className={`cui-dot ${speaking ? "cui-dot-live" : ""}`} />
      </div>

      {/* ── Transcript ── */}
      <div className="cui-transcript" ref={scrollEl} aria-live="polite" aria-label="Call transcript">
        {msgs.length === 0
          ? <p className="cui-empty">Transcript will appear here…</p>
          : msgs.map(m => (
            <div key={m.id} className={`cui-bubble cui-${m.role}`}>
              <span className="cui-bubble-who">{m.role === "agent" ? "Agent" : "You"}</span>
              <p className="cui-bubble-text">{m.text}</p>
            </div>
          ))
        }
      </div>

      {/* ── Info bar ── */}
      <div className="cui-info">
        <span className="cui-info-tag">{subtitle}</span>
      </div>

      {/* ── Controls ── */}
      <div className="cui-controls">
        <button
          className={`cui-btn cui-mute ${!isMicrophoneEnabled ? "cui-muted" : ""}`}
          onClick={toggleMic}
          aria-label={isMicrophoneEnabled ? "Mute" : "Unmute"}
        >
          <MicIcon muted={!isMicrophoneEnabled} />
          <span className="cui-btn-label">{isMicrophoneEnabled ? "Mute" : "Unmute"}</span>
        </button>

        <button className="cui-btn cui-hangup" onClick={hangUp} aria-label="End call">
          <HangUpIcon />
          <span className="cui-btn-label">End call</span>
        </button>
      </div>
    </div>
  );
}

// ── Waveform ──────────────────────────────────────────────────────────────────
function Waveform({ active }: { active: boolean }) {
  return (
    <div className={`wave ${active ? "wave-active" : ""}`} aria-hidden="true">
      {[0,1,2,3,4].map(i => <span key={i} className="wave-bar" style={{ animationDelay:`${i*0.09}s` }} />)}
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────
function MicIcon({ muted }: { muted: boolean }) {
  return muted
    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>;
}

function HangUpIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.42 19.42 0 0 1 3.07 8.63 19.79 19.79 0 0 1 0 0a2 2 0 0 1 2-2.18"/><line x1="23" y1="1" x2="1" y2="23"/></svg>;
}
